import React from 'react';
import { BRAND } from '../../lib/brand';
import SourcesEditor from './SourcesEditor';
import {
  DOMAINS, defaultBot, parseUrlList, provisionPayload, slugify, sourceFromProbe, sourceProblems,
} from './onboarding';
import {
  ErrorList, Field, apiErrors, badge, btnPrimary, btnSmall, card, hint, inputStyle,
} from './ui';

// Onboard a client: name and sites -> probe each site -> review how each is
// crawled -> bot basics -> launch (one provision call that stores the crawl
// config, domain and bot settings, then fires the first crawl). The draft
// survives a reload (sessionStorage) until launched or discarded.

const STEPS = [
  { id: 'client', label: 'Client' },
  { id: 'sites', label: 'Sites' },
  { id: 'bot', label: 'Assistant' },
  { id: 'review', label: 'Review & launch' },
];
const DRAFT_KEY = 'opsOnboardDraft';
// No kind is preselected: an operator who skips it must be stopped, not handed
// a default. (Unset on the server means pension; Mooresville ran 11 days that way.)
const EMPTY = {
  step: 'client', name: '', slug: '', slugTouched: false, domain: '', urlsText: '',
  rows: [], channel: '', bot: defaultBot(''), addUrl: '',
};
const TRANSIT_EXAMPLES = ['How do I get from downtown to the airport?', 'When is the next bus at the transit center?'];

function loadDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    return EMPTY;
  }
}

function saveDraft(state) {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state)); } catch { /* private mode: no draft */ }
}

function clearDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

function Stepper({ step }) {
  const at = STEPS.findIndex((s) => s.id === step);
  return (
    <ol style={{ display: 'flex', gap: '18px', listStyle: 'none', padding: 0, margin: '0 0 18px', flexWrap: 'wrap' }}>
      {STEPS.map((s, i) => (
        <li key={s.id} aria-current={s.id === step ? 'step' : undefined}
            style={{ fontSize: '0.82rem', fontWeight: s.id === step ? 700 : 500,
                     color: i < at ? '#065f46' : s.id === step ? BRAND.ink900 : BRAND.gray400 }}>
          {i < at ? '✓ ' : `${i + 1}. `}{s.label}
        </li>
      ))}
    </ol>
  );
}

export default function OnboardWizard({ api, onLaunched, onClose }) {
  const [s, setS] = React.useState(loadDraft);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  const update = (patch) => setS((prev) => {
    const next = { ...prev, ...patch };
    saveDraft(next);
    return next;
  });

  const probe = async (urls) => {
    const { ok, data } = await api('/api/admin/ops/probe', { method: 'POST', body: JSON.stringify({ urls }) });
    if (!ok) throw new Error(apiErrors(data, 'probe failed'));
    return data.results || [];
  };

  const lookAtSites = async () => {
    const urls = parseUrlList(s.urlsText);
    setError(null);
    if (!s.name.trim()) return setError('Enter the client name.');
    if (!s.domain) return setError('Choose the kind of organization. It decides which guards run and how answers are worded.');
    if (!urls.length) return setError('Enter at least one site URL.');
    setBusy(true);
    try {
      const results = await probe(urls);
      const rows = results.map(sourceFromProbe);
      const main = rows.find((r) => r.include);
      const phones = (main && main.probe.phones) || [];
      update({
        step: 'sites', rows, channel: main ? main.site : '',
        bot: { ...defaultBot(s.name.trim(), phones), ...(s.bot.display_name ? s.bot : {}) },
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addSite = async () => {
    const url = s.addUrl.trim();
    if (!url) return;
    setBusy(true);
    setError(null);
    try {
      const [r] = await probe([url]);
      update({ rows: [...s.rows, sourceFromProbe(r)], addUrl: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = provisionPayload({ ...s, rows: s.rows });
      const { ok, status, data } = await api('/api/admin/ops/provision', { method: 'POST', body: JSON.stringify(payload) });
      if (!ok) {
        // 409: the tenant id is taken, or the main site's corpus already has
        // tenants (its settings are edited from their Settings, never here).
        setError(status === 409 && !data.existing_sites
          ? `${data.error}. Pick another tenant id, or edit the existing tenant from its Settings.`
          : apiErrors(data, 'provision failed'));
        return;
      }
      clearDraft();
      setResult(data);
      if (onLaunched) onLaunched(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    clearDraft();
    setS(EMPTY);
    setError(null);
  };

  const problems = s.step === 'sites' ? sourceProblems(s.rows) : [];
  const channelIncluded = s.rows.some((r) => r.include && r.site === s.channel);
  const slug = s.slugTouched ? s.slug : slugify(s.name);

  if (result) {
    return (
      <section style={{ ...card, padding: '20px', marginBottom: '28px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 10px' }}>{s.name || 'Client'} is onboarding</h2>
        <p style={{ fontSize: '0.88rem' }}>
          Demo: <a href={result.demo_url} target="_blank" rel="noreferrer">{result.demo_url}</a>
          <span style={badge('#dbeafe', '#1e40af')}>first crawl: {result.initial_crawl}</span>
        </p>
        {!result.customer_reused && result.customer_key && (
          <p style={{ fontSize: '0.84rem' }}>
            New customer key (save it): <code>{result.customer_key}</code>{' '}
            <button type="button" style={btnSmall} onClick={() => navigator.clipboard.writeText(result.customer_key)}>Copy key</button>
          </p>
        )}
        <ul style={{ fontSize: '0.84rem', color: BRAND.gray600, paddingLeft: '18px' }}>
          <li>The crawl runs site by site; document counts per site appear on the tenant row as it goes. Large town sites take about an hour.</li>
          <li>The demo answers from whatever is indexed so far, and declines until the first site lands.</li>
          {result.domain === 'transit' && <li><b>Next:</b> open the tenant's Settings → Transit to connect the GTFS feed and review landmarks.</li>}
          <li>Change sites, excludes or the assistant later from the tenant's Settings.</li>
        </ul>
        <button type="button" style={btnPrimary} onClick={() => { setResult(null); setS(EMPTY); if (onClose) onClose(); }}>Done</button>
      </section>
    );
  }

  return (
    <section style={{ ...card, padding: '20px', marginBottom: '28px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Onboard a client</h2>
        <span style={{ flex: 1 }} />
        {(s.name || s.urlsText) && <button type="button" style={btnSmall} onClick={discard}>Discard draft</button>}
        {onClose && <button type="button" style={btnSmall} onClick={onClose}>Close</button>}
      </div>
      <Stepper step={s.step} />

      {s.step === 'client' && (
        <>
          <Field id="onboard-name" label="Client name">
            <input id="onboard-name" value={s.name} placeholder="Town of Mooresville" style={inputStyle}
                   onChange={(e) => update({ name: e.target.value })} />
          </Field>
          <Field id="onboard-slug" label="Tenant id" help={`The demo will be ${slug || '<id>'}-demo at /chat-demo/${slug || '<id>'}-demo.`}>
            <input id="onboard-slug" value={slug} style={inputStyle}
                   onChange={(e) => update({ slug: slugify(e.target.value), slugTouched: true })} />
          </Field>
          <Field id="onboard-domain" label="Kind of organization"
                 help={DOMAINS.find((d) => d.id === s.domain)?.help || 'Decides which guards run and how answers are worded.'}>
            <select id="onboard-domain" value={s.domain} style={inputStyle} onChange={(e) => update({ domain: e.target.value })}>
              <option value="" disabled>Choose…</option>
              {DOMAINS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </Field>
          <Field id="onboard-urls" label="Websites to search"
                 help="One per line. Put the main site first; add sister sites (events, venues, golf course…) below it.">
            <textarea id="onboard-urls" rows={5} value={s.urlsText} style={{ ...inputStyle, fontFamily: 'monospace' }}
                      placeholder={'https://www.mooresvillenc.gov\nhttps://www.mooresvilleevents.com'}
                      onChange={(e) => update({ urlsText: e.target.value })} />
          </Field>
          <ErrorList error={error} />
          <button type="button" style={btnPrimary} disabled={busy} onClick={lookAtSites}>
            {busy ? 'Looking at the sites…' : 'Look at these sites'}
          </button>
        </>
      )}

      {s.step === 'sites' && (
        <>
          <p style={{ ...hint, marginBottom: '12px' }}>
            Check each site is what you expect (what it <i>says it is</i>), pick how it's crawled, and keep out anything
            stale or login-only. The main site's URL becomes the corpus id.
          </p>
          <SourcesEditor rows={s.rows} onChange={(rows) => update({ rows })}
                         channel={s.channel} onChannel={(channel) => update({ channel })} />
          <div style={{ display: 'flex', gap: '8px', margin: '4px 0 12px' }}>
            <input aria-label="Another site URL" value={s.addUrl} placeholder="Add another site: https://…"
                   style={{ ...inputStyle, marginBottom: 0 }} onChange={(e) => update({ addUrl: e.target.value })} />
            <button type="button" style={btnSmall} disabled={busy || !s.addUrl.trim()} onClick={addSite}>
              {busy ? 'Looking…' : 'Add'}
            </button>
          </div>
          <ErrorList error={error || (problems.length ? problems : (!channelIncluded ? 'Pick the main site.' : null))} />
          <button type="button" style={btnSmall} onClick={() => update({ step: 'client' })}>Back</button>
          <button type="button" style={btnPrimary} disabled={problems.length > 0 || !channelIncluded}
                  onClick={() => { setError(null); update({ step: 'bot' }); }}>
            Next
          </button>
        </>
      )}

      {s.step === 'bot' && (
        <>
          <Field id="onboard-display-name" label="Assistant name">
            <input id="onboard-display-name" value={s.bot.display_name} style={inputStyle}
                   onChange={(e) => update({ bot: { ...s.bot, display_name: e.target.value } })} />
          </Field>
          <Field id="onboard-starter-1" label="Starter questions"
                 help={`Up to four, shown before the first question. Pick ones the sites answer plainly; check each on the demo once the crawl lands.${
                   s.domain === 'transit' ? ` Transit ideas: “${TRANSIT_EXAMPLES.join('”, “')}”.` : ''}`}>
            {s.bot.starter_questions.map((q, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <input key={i} id={`onboard-starter-${i + 1}`} aria-label={`Starter question ${i + 1}`} value={q}
                     style={{ ...inputStyle, marginBottom: '6px' }}
                     onChange={(e) => update({ bot: { ...s.bot, starter_questions: s.bot.starter_questions.map((x, j) => (j === i ? e.target.value : x)) } })} />
            ))}
          </Field>
          <Field id="onboard-contact" label="How to reach the organization"
                 help="Fills “please contact {agency} at …” in replies. A phone number reads best.">
            <input id="onboard-contact" value={s.bot.contact_text} style={inputStyle} placeholder="e.g. 555-123-4567"
                   onChange={(e) => update({ bot: { ...s.bot, contact_text: e.target.value } })} />
          </Field>
          <Field id="onboard-refusal" label="When the answer isn't on the sites (optional)"
                 help="Replaces the built-in reply. {agency} and {contact} are filled in. Use it to point to sources the crawler can't read, such as a Municode code of ordinances.">
            <textarea id="onboard-refusal" rows={3} value={s.bot.refusal_message} style={inputStyle}
                      onChange={(e) => update({ bot: { ...s.bot, refusal_message: e.target.value } })} />
          </Field>
          <button type="button" style={btnSmall} onClick={() => update({ step: 'sites' })}>Back</button>
          <button type="button" style={btnPrimary} onClick={() => update({ step: 'review' })}>Next</button>
        </>
      )}

      {s.step === 'review' && (() => {
        const payload = provisionPayload({ ...s, rows: s.rows });
        return (
          <>
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', fontSize: '0.86rem', margin: '0 0 12px' }}>
              <dt style={{ color: BRAND.gray500 }}>Demo tenant</dt><dd style={{ margin: 0 }}><code>{payload.slug}-demo</code></dd>
              <dt style={{ color: BRAND.gray500 }}>Main site (corpus)</dt><dd style={{ margin: 0 }}>{payload.site_url}</dd>
              <dt style={{ color: BRAND.gray500 }}>Kind</dt>
              <dd style={{ margin: 0 }}>{DOMAINS.find((d) => d.id === payload.domain)?.label || 'Not chosen (go back to Client)'}</dd>
              <dt style={{ color: BRAND.gray500 }}>Sites</dt>
              <dd style={{ margin: 0 }}>
                {payload.crawl.sources.map((src) => (
                  <div key={src.site}>
                    {src.site} — {src.mode}{src.linkedPdfs ? ' + linked PDFs' : ''}
                    {src.exclude ? ` · ${src.exclude.length} exclude rule(s)` : ''}
                  </div>
                ))}
              </dd>
              <dt style={{ color: BRAND.gray500 }}>Assistant</dt>
              <dd style={{ margin: 0 }}>
                {payload.widget.display_name || '(default name)'} · {payload.widget.starter_questions.length} starter question(s)
                {payload.persona.contact_text ? ` · contact ${payload.persona.contact_text}` : ''}
              </dd>
            </dl>
            <details style={{ marginBottom: '12px' }}>
              <summary style={{ fontSize: '0.8rem', color: BRAND.gray500, cursor: 'pointer' }}>Request body</summary>
              <pre style={{ fontSize: '0.74rem', background: '#f8fafc', padding: '10px', borderRadius: '6px', overflowX: 'auto' }}>
                {JSON.stringify(payload, null, 2)}
              </pre>
            </details>
            <ErrorList error={error} />
            <button type="button" style={btnSmall} onClick={() => update({ step: 'bot' })}>Back</button>
            <button type="button" style={btnPrimary} disabled={busy || !payload.domain} onClick={launch}>
              {busy ? 'Launching…' : 'Create demo and start crawling'}
            </button>
          </>
        );
      })()}
    </section>
  );
}
